import { useSignal } from "@preact/signals";
import { lazy, Suspense, useEffect } from "preact/compat";
import AddToCartButton from "./AddToCartButton.tsx";
import Icon from "$store/components/ui/Icon.tsx";
import QuantitySelector from "$store/components/ui/QuantitySelector.tsx";
import type { ToAddToCart } from "$store/sdk/ga4/events/add_to_cart.ts";

const SkuSelector = lazy(() => import("./SkuSelector.tsx"));

interface Props {
  sku: string;
  name?: string;
  productGroupID?: string;
  sellerId?: string;
  discount?: number;
  price?: number;
  variants?: [string, { available: boolean; url: string; id: number }[]][];
  inventoryLevel?: number;
  analytics?: ToAddToCart;
  isMobileDevice: boolean;
}

export default function ShelfAddToCart(
  {
    sku,
    name = "",
    productGroupID = "",
    sellerId = "1",
    discount = 0,
    price = 0,
    variants,
    inventoryLevel,
    analytics,
    isMobileDevice,
  }: Props,
) {
  const quantity = useSignal(1);
  const hasVariants = (variants?.length ?? 0) > 1;
  const selectedSku = useSignal(hasVariants ? "" : sku);
  const showSkuSelector = useSignal(false);

  useEffect(() => {
    selectedSku.value = (variants?.length) ?? 0 > 1 ? "" : sku;
    showSkuSelector.value = false;
    quantity.value = 1;
  }, [sku]);

  return (
    <div class="flex items-center gap-2 group-data-[mode=list]/gallery:md:col-start-2 group-data-[mode=list]/gallery:md:row-start-1 group-data-[mode=list]/gallery:md:row-end-6">
      {!isMobileDevice && (
        <QuantitySelector
          quantity={quantity.value}
          max={inventoryLevel}
          onChange={(q) => quantity.value = q <= 0 ? 1 : q}
        />
      )}

      <AddToCartButton
        class="w-full h-12 rounded-lg bg-tw-secondary-200 text-white text-xl font-bold transition-colors duration-300 flex justify-center items-center gap-2 hover:bg-tw-secondary-300"
        skuId={selectedSku.value}
        name={name}
        productGroupId={productGroupID}
        sellerId={sellerId}
        discount={discount}
        price={price}
        quantity={quantity.value}
        onClick={() => {
          if (!hasVariants) return;

          showSkuSelector.value = !showSkuSelector.value;
          selectedSku.value = "";
        }}
        analytics={analytics && {
          ...analytics,
          items: analytics.items.map((item) => ({
            ...item,
            extended: {
              ...item.extended,
              quantity: quantity.value,
            },
          })),
        }}
      >
        {!isMobileDevice && (
          <Icon
            id="Cart"
            class="hidden xl:block"
            width={25}
            height={22}
          />
        )}

        Comprar
      </AddToCartButton>

      {hasVariants &&
        (
          <Suspense
            fallback={
              <div class="h-20 py-3 px-2 bg-tw-primary-100 rounded-lg absolute top-0 left-0 right-0">
                <span class="loading loading-ring" />
              </div>
            }
          >
            {showSkuSelector.value &&
              (
                <SkuSelector
                  variants={variants!}
                  selected={selectedSku.value}
                  onClick={(skuId) => {
                    selectedSku.value = skuId !== selectedSku.value
                      ? skuId
                      : "";
                  }}
                />
              )}
          </Suspense>
        )}
    </div>
  );
}
